import type { GrammarTopic } from './types';

export const everydayTopics: GrammarTopic[] = [
  {
    slug: 'present-continuous',
    title: 'Present Continuous',
    blocks: [
      {
        kind: 'p',
        text: 'The present continuous is a verb tense that is used to show that an ongoing action is happening now, at the moment of speech or now in a larger sense.',
      },
      {
        kind: 'p',
        text: '1. To talk about affirmative ongoing actions, we use TO BE (Is, are, am) + VERB IN THE -ING:',
      },
      {
        kind: 'examples',
        items: [
          'Luisa **is paying** attention to the explanation.',
          'People **are informing** themselves about mental health problems.',
          'I **am learning** the present continuous.',
        ],
      },
      {
        kind: 'p',
        text: '2. To talk about ongoing actions that are NOT happening, we use TO BE + NOT + VERB IN THE -ING:',
      },
      {
        kind: 'examples',
        items: [
          'People **aren’t learning** about their mental health. (are + not)',
          'Julia **isn’t sleeping** enough at night. (is + not)',
          'I **am not having** enough sleep.',
        ],
      },
      {
        kind: 'p',
        text: '3. To make a question about ongoing actions, we use TO BE + SUBJECT (I, You, He, She, It, We, They) + VERB IN THE -ING:',
      },
      {
        kind: 'examples',
        items: [
          '**Are** you **taking** care of your mental health?',
          '**Am** I **being** conscious of my mental health?',
          '**Is** she **understanding** the Present Continuous?',
          '**Is** Isabella **feeling** ok today?',
        ],
      },
    ],
  },
  {
    slug: 'adding-ing-gerund',
    title: 'Adding -ing to Form the Gerund',
    blocks: [
      { kind: 'p', text: 'a. The general rule is to add -ing to the verb.' },
      {
        kind: 'examples',
        items: ['wait → **waiting**', 'visit → **visiting**', 'speak → **speaking**'],
      },
      { kind: 'p', text: 'b. When a verb ends in -e, we drop the e and add -ing.' },
      {
        kind: 'examples',
        items: ['close → **closing**', 'hate → **hating**', 'love → I’m **loving** it.'],
      },
      {
        kind: 'p',
        text: 'c. For verbs ending in a CVC (consonant, vowel, consonant), we double the final consonant and add -ing.',
      },
      {
        kind: 'examples',
        items: ['run → **running**', 'stop → **stopping**', 'hit → **hitting**', 'travel → **travelling**'],
      },
      { kind: 'p', text: 'd. For verbs ending in -ie, we drop the -ie, and add -ying.' },
      { kind: 'examples', items: ['die → **dying**', 'lie → **lying**'] },
    ],
  },
  {
    slug: 'simple-future',
    title: 'The Simple Future',
    blocks: [
      {
        kind: 'p',
        text: 'The simple future is used to refer to actions or states that start and finish in the future. These events have not happened yet but will happen sometime in the future.',
      },
      { kind: 'h3', text: 'WILL + main verb in the base form' },
      {
        kind: 'p',
        text: 'We use the simple future tense to make predictions or guesses about the future.',
      },
      {
        kind: 'examples',
        items: [
          'I **will meet** her at the shopping mall.',
          'It **will rain** tomorrow.',
          'I think that our team **will win** the baseball game.',
        ],
      },
      {
        kind: 'p',
        text: 'To make the negative, we use the auxiliary verb WILL + NOT for all subjects. The contraction of WILL + NOT is WON’T.',
      },
      {
        kind: 'examples',
        items: [
          'I **will not meet** her at the shopping mall.',
          'It **won’t rain** tomorrow.',
          'I think that our team **won’t win** the baseball game.',
        ],
      },
      {
        kind: 'p',
        text: 'To ask questions about the future, we need to invert the subject and auxiliary verb WILL.',
      },
      {
        kind: 'examples',
        items: [
          '**Will** you **marry** me? Yes, I **will marry** you.',
          '**Will** I **see** you again? No, you **won’t see** me again.',
        ],
      },
    ],
  },
  {
    slug: 'will-vs-going-to',
    title: 'Will vs. Going To',
    blocks: [
      {
        kind: 'p',
        text: 'Many English learners feel confused when learning about the future forms in English because there are two ways of making it: using the auxiliary will or using the auxiliary going to. Let’s explore their differences!',
      },
      {
        kind: 'p',
        text: '1. We use WILL for talking about decisions made in the moment, promises, or offers, and it doesn’t necessarily indicate a pre-planned action.',
      },
      {
        kind: 'examples',
        items: [
          'From now on, I **will treat** myself with more kindness.',
          '**I\'ll give** you a discount if you buy it right now.',
        ],
      },
      {
        kind: 'p',
        text: '2. We use GOING TO for talking about decisions or plans that have already been made. It’s about intentions and things that are likely to happen because they’ve been considered before.',
      },
      {
        kind: 'examples',
        items: [
          'I’m **going to start** journaling every night because I want to understand my emotions better.',
          'They\'re **going to retire** to the beach - in fact they have already bought a little beach house.',
        ],
      },
      { kind: 'h3', text: 'To sum up…' },
      { kind: 'p', text: 'If someone asks: "Are you busy this evening?"' },
      {
        kind: 'list',
        items: [
          '→ If I respond: "Yes, I\'m **going to go** to the movies." I use GOING TO because it is a plan I made earlier (before I was asked the question). - In this case we cannot use WILL.',
          '→ If I haven\'t made plans, then you can say either: "I **will** probably **watch** TV." OR "I\'m probably **going to watch** TV." → Both WILL and GOING TO are possible in this situation because we are predicting what will happen (since we haven\'t made any plans).',
        ],
      },
    ],
  },
  {
    slug: 'adverbs-of-intensity',
    title: 'Adverbs of Intensity (Intensifiers)',
    blocks: [
      {
        kind: 'p',
        text: 'We use adverbs of degree when we show the intensity of an action (verb), adjective or another adverb.',
      },
      { kind: 'p', text: 'The main adverbs of degree are:' },
      {
        kind: 'list',
        items: [
          'Almost (nearly, approximately)',
          'Extremely (to a very great intensity)',
          'Completely (no less or fewer than)',
          'Quite (enough)',
          'Pretty (more than enough, in a high intensity)',
          'Too (more than it should be)',
          'Very (in a high intensity)',
        ],
      },
      {
        kind: 'examples',
        items: [
          'Your dinner is **almost** ready.',
          'I have an **extremely** busy work schedule.',
          'My boss is **completely** tired.',
          'This topic is **quite** interesting.',
          'Studying English is **pretty** useful.',
          'Her dress was **too** small for me.',
          'My sister is **very** smart.',
        ],
      },
    ],
  },
  {
    slug: 'simple-past',
    title: 'The Simple Past',
    blocks: [
      {
        kind: 'p',
        text: 'The simple past tense is used to talk about a completed action in a time before now. The time of the action can be in the recent past or the distant past.',
      },
      {
        kind: 'examples',
        items: [
          '“Where **did** you **go** on your last vacation?” “On my last vacation, I **went** to a resort with my family.”',
          '“What **did** you **do**?” “We **enjoyed** the hotel, and we **visited** the beaches.”',
        ],
      },
      { kind: 'h3', text: 'HOW TO FORM THE SIMPLE PAST' },
      {
        kind: 'p',
        text: 'The auxiliary verb of the Simple Past is the verb “DID”. We use them to make questions and negative statements.',
      },
      {
        kind: 'table',
        header: ['Form', 'Structure', 'Example'],
        rows: [
          ['AFFIRMATIVE', 'Subject + verb in the past form', 'I **loved** him.'],
          ['NEGATIVE', 'Subject + did not (didn’t) + verb in the infinitive', 'I **didn’t love** him.'],
          ['INTERROGATIVE', 'Did + subject + verb in the infinitive', '**Did** you **love** him?'],
        ],
      },
      { kind: 'h3', text: 'REGULAR VERBS' },
      { kind: 'p', text: 'The regular verbs usually end in -ed.' },
      {
        kind: 'table',
        header: ['Rule', 'Base form', 'Simple past'],
        rows: [
          ['GENERAL RULE', 'work, walk, wait', 'worked, walked, waited, wanted, decided'],
          ['VERBS ENDED IN -E', 'arrive, like, love', 'arrived, liked, loved'],
          ['VERBS ENDED IN CVC', 'plan, stop, prefer', 'planned, stopped, preferred'],
          ['VERBS ENDED IN CONSONANT + Y', 'study, cry', 'studied, cried'],
          ['VERBS ENDED IN VOWEL +Y', 'play, stay', 'played, stayed'],
        ],
      },
      { kind: 'h3', text: 'IRREGULAR VERBS' },
      {
        kind: 'p',
        text: 'The irregular verbs don’t follow a pattern. Then, it’s important to know some of them.',
      },
      {
        kind: 'table',
        header: ['INFINITIVE FORM', 'SIMPLE PAST'],
        rows: [
          ['be', 'was (I, he, she, it) | were (you, we, they)'],
          ['to have', 'had'],
          ['to do', 'did'],
          ['to say', 'said'],
          ['to go', 'went'],
          ['to get', 'got'],
          ['to make', 'made'],
          ['to know', 'knew'],
          ['to think', 'thought'],
          ['to take', 'took'],
          ['to see', 'saw'],
          ['to come', 'came'],
          ['to find', 'found'],
          ['to give', 'gave'],
          ['to tell', 'told'],
          ['to feel', 'felt'],
          ['to become', 'became'],
          ['to leave', 'left'],
          ['to put', 'put'],
          ['to mean', 'meant'],
          ['to keep', 'kept'],
          ['to let', 'let'],
          ['to begin', 'began'],
          ['to show', 'showed'],
          ['to hear', 'heard'],
          ['run', 'ran'],
          ['to hold', 'held'],
          ['to bring', 'brought'],
          ['to write', 'wrote'],
          ['to sit', 'sat'],
          ['to stand', 'stood'],
          ['to lose', 'lost'],
          ['to pay', 'paid'],
          ['to meet', 'met'],
          ['to set', 'set'],
          ['to learn', 'learnt / learned'],
          ['to lead', 'led'],
          ['to understand', 'understood'],
          ['to speak', 'spoke'],
          ['to read', 'read'],
          ['to spend', 'spent'],
          ['to grow', 'grew'],
          ['to win', 'won'],
          ['to buy', 'bought'],
          ['to send', 'sent'],
          ['to build', 'built'],
          ['to fall', 'fell'],
          ['to cut', 'cut'],
        ],
      },
      { kind: 'h3', text: 'Pronunciation point: the -ed endings' },
      {
        kind: 'p',
        text: 'In English, there are three different pronunciations of the –ed ending for regular past tense verbs: /ɪd/ (wanted, ended), /d/ (played, bored), and /t/ (watched, jumped). The pronunciation depends on the final sound in the verb.',
      },
      {
        kind: 'note',
        text: 'See the full explanation and the sound chart in the “-ed Pronunciation” topic.',
      },
    ],
  },
  {
    slug: 'ed-pronunciation',
    title: '-ed Pronunciation',
    blocks: [
      {
        kind: 'p',
        text: 'In English, there are three different pronunciations of the –ed ending for regular past tense verbs. The pronunciation of the –ed ending depends on the final sound in the verb:',
      },
      { kind: 'h3', text: '1. TED-DED' },
      {
        kind: 'p',
        text: 'If the verb ends in a –d or a –t, the –ed ending is pronounced as a new syllable, /id/.',
      },
      { kind: 'examples', items: ['**afforded**, **wanted**, **ended**, **visited**, **branded**'] },
      {
        kind: 'p',
        text: '2. If the verb ends in a vowel sound or a voiced consonant (l, n, r, b, g, m, z, v, y), then the –ed ending sounds like a /d/ and does not create a new syllable',
      },
      { kind: 'examples', items: ['**bored**, **caused**, **played**'] },
      {
        kind: 'p',
        text: '3. If the verb ends in an unvoiced/voiceless consonant (p, k, f, gh, sh, ch, ss, c, x), then the –ed ending sounds like a /t/ and does not create a new syllable.',
      },
      { kind: 'examples', items: ['**jumped**, **produced**, **watched**'] },
      {
        kind: 'table',
        header: [
          'If the base verb ends in one of these sounds',
          'Example base verb*',
          'Example with -ed',
          'Pronounce the -ed as',
        ],
        rows: [
          ['/t/', 'want', 'wanted', '/ɪd/'],
          ['/d/', 'end', 'ended', '/ɪd/'],
          ['unvoiced /p/', 'hope', 'hoped', '/t/'],
          ['unvoiced /f/', 'laugh', 'laughed', '/t/'],
          ['unvoiced /s/', 'fax', 'faxed', '/t/'],
          ['unvoiced /ʃ/', 'wash', 'washed', '/t/'],
          ['unvoiced /ʧ/', 'watch', 'watched', '/t/'],
          ['unvoiced /k/', 'like', 'liked', '/t/'],
          ['voiced, all other sounds', 'play', 'played', '/d/'],
          ['voiced, all other sounds', 'allow', 'allowed', '/d/'],
          ['voiced, all other sounds', 'beg', 'begged', '/d/'],
        ],
      },
      {
        kind: 'note',
        text: '*Note that it is the sound that is important, not the letter or spelling. For example, fax ends in the letter x but the sound /s/; like ends in the letter e but the sound /k/.',
      },
    ],
  },
  {
    slug: 'genitive-case',
    title: 'Possessive Forms: The Genitive Case',
    blocks: [
      {
        kind: 'p',
        text: 'The genitive case is a grammatical case for nouns and pronouns. It is most commonly used for showing possession. Typically, forming the genitive case involves adding an apostrophe followed by “s” to the end of a noun.',
      },
      {
        kind: 'examples',
        items: [
          'I borrowed a calculator. The calculator belongs to Sam. -> I borrowed **Sam’s** calculator.',
        ],
      },
      { kind: 'h3', text: 'HOW TO FORM THE POSSESSIVE GENITIVE CASE' },
      { kind: 'h3', text: '1. SINGULAR NOUNS' },
      { kind: 'p', text: 'We add ’s to the possessor.' },
      {
        kind: 'examples',
        items: [
          'My **mother’s** house is next to the beach. (= the house of my mother)',
          'Tomorrow, we’re all going to see the **museum’s** art exhibit. (= the art exhibit of the museum)',
          'The **children\'s** toys are all over the place. (= The toys of the children)',
        ],
      },
      { kind: 'h3', text: '2. PLURAL NOUNS ENDING IN -S' },
      { kind: 'p', text: 'We only add the apostrophe ‘.' },
      {
        kind: 'examples',
        items: [
          'The two **sisters’** house is next to mine. (= the house of the two sisters)',
          'The **players’** boots were dirty after the game. (= the boots of the players)',
        ],
      },
    ],
  },
  {
    slug: 'english-vowel-sounds',
    title: 'English Vowel Sounds',
    blocks: [
      {
        kind: 'p',
        text: 'In English, vowel sounds can change the meaning of a word. One important distinction is between short vowels and long vowels.',
      },
      { kind: 'p', text: 'Vowel sounds are divided into the following three categories:' },
      {
        kind: 'list',
        items: [
          'Long vowels (vowels that sound like the letter name)',
          'Short vowels (the most common sound for a single vowel spelling)',
          'Other vowels (the remaining vowel sounds)',
        ],
      },
      { kind: 'p', text: 'Short vowels are quick, relaxed sounds.' },
      {
        kind: 'list',
        items: [
          '/ɪ/ → **fit** /fɪt/, **pick** /pɪk/, **difficult** /ˈdɪ.fɪ.kəlt/',
          '/e/ → **pet** /pet/, **sent** /sent/, **attention** /əˈten.ʃən/',
          '/æ/ → **pat** /pæt/, **flat** /flæt/, **family** /ˈfæ.mə.li/',
          '/ʌ/ → **cut** /kʌt/ **jump** /dʒʌmp/, **cover** /ˈkʌ.vər/',
          '/ʊ/ → **put** /pʊt/, **book** /bʊk/, **cushion** /ˈkʊ.ʃən/',
          '/ɒ/ → **pot** /pɒt/, **dog** /dɒg/, **hospital** /ˈhɒs.pɪ.təl/',
          '/ə/ → **about** /əˈbaʊt/, **system** /ˈsɪs.təm/, **complete** /kəmˈpliːt/',
        ],
      },
      {
        kind: 'p',
        text: 'Long vowels are longer and tenser sounds, often similar to the letter name.',
      },
      {
        kind: 'list',
        items: [
          '/i:/ → **week** /wi:k/, **feet** /fi:t/, **media** /ˈmiː.di.jə/',
          '/ɑ:/ → **hard** /ha:/, **park** /pa:k/, **article** /ɑː.tɪ.kəl/',
          '/ɔ:/ → **fork** /fɔ:k/, **walk** /wɔ:k/, **August** /ɔːˈɡʌst/',
          '/ɜ:/ → **heard** /hɜ:d/, **word** /wɜ:d/, **surface** /ˈsɜː.fɪs/',
          '/u:/ → **boot** /bu:t/, **group** /gru:p/, **beautiful** /ˈbjuː.tɪ.fəl/',
        ],
      },
      { kind: 'h3', text: 'Why does this matter?' },
      { kind: 'p', text: 'Changing the vowel sound can change the meaning:' },
      {
        kind: 'list',
        items: [
          '**ship** /ɪ/ vs **sheep** /iː/',
          '**full** /ʊ/ vs **fool** /uː/',
          '**cut** /ʌ/ vs **cart** /ɑː/',
        ],
      },
      { kind: 'h3', text: 'Other Vowels' },
      { kind: 'p', text: 'Diphthong vowels (double) are:' },
      {
        kind: 'list',
        items: [
          '/eɪ/ → **place** /pleɪs/, **late** /leɪt/, **dangerous** /ˈdeɪn.dʒə.rəs/',
          '/oʊ/ → **home** /hoʊm/, **phone** /foʊn/, **global** /ˈɡloʊ.bəl/',
          '/aʊ/ → **mouse** /maʊs/, **brown** /braʊn/, **accountant** /əˈkaʊn.t̬ənt/',
          '/ɪə/ → **clear** /klɪə/, **fear** /fɪə/, **career** /kəˈrɪə/',
          '/eə/ → **care** /keə/, **wear** /weə/, **declare** /dɪˈkleə/',
          '/ɔɪ/ → **boy** /bɔɪ/, **toy** /tɔɪ/, **enjoyable** /ɪnˈdʒɔɪ.jə.bəl/',
          '/aɪ/ → **find** /faɪnd/, **bite** /baɪt/, **tiger** /ˈtaɪ.ɡə/',
          '/ʊə/ → **tour** /tʊə/, **pure** /pʊə/, **mature** /məˈtʃʊə/',
          '/iu/ → **user**, **university**, **union**, **European**',
          '/ua/ → **one**',
        ],
      },
      {
        kind: 'note',
        text: 'All these diphthongs start with a vowel and are followed by a semivowel.',
      },
    ],
  },
  {
    slug: 'a-or-an',
    title: 'A or An?',
    blocks: [
      {
        kind: 'p',
        text: 'Now that you know the vowels in English, it is time to revisit a simple rule: the use of A and AN. The choice of the indefinite article is not determined by the initial letter of the next word, but rather by the initial sound.',
      },
      { kind: 'p', text: 'If the word starts with a vowel sound, we should use AN.' },
      { kind: 'examples', items: ['**an apple**, **an hour**, **an elephant**'] },
      { kind: 'p', text: 'If it starts with a consonant sound, we should use A.' },
      { kind: 'examples', items: ['**a boy**, **a zoo**, **a horse**'] },
      {
        kind: 'p',
        text: 'If it starts with a semivowel sound (a vowel that is weaker than the next vowel), we should use A.',
      },
      { kind: 'examples', items: ['**a user**, **a university**, **a European**, **a one-night stand**'] },
    ],
  },
  {
    slug: 'cardinal-numbers',
    title: 'Numbers: Cardinal Numbers',
    blocks: [
      {
        kind: 'p',
        text: 'Counting is the foundation of numerical comprehension. Let’s start by reviewing numbers 1 to 10 with their English spellings.',
      },
      {
        kind: 'p',
        text: 'Cardinal numbers are generally used for counting and expressing quantities. They are often used to:',
      },
      {
        kind: 'list',
        items: [
          'a. Give your age in years: “I’m **26** years old.”',
          'b. Give your telephone number: “My phone number is **five-five-five-six-four-three-two**.”',
        ],
      },
      {
        kind: 'p',
        text: 'When writing in English, it’s best to spell out cardinal numbers one to nine, but we use the numeral itself to express large numbers.',
      },
      {
        kind: 'examples',
        items: [
          'I have **two** siblings.',
          'I bought **eight** books for my brother.',
          'There are **11** students in the class.',
          'About **101** people signed up for the training.',
        ],
      },
      { kind: 'h3', text: 'Counting beyond 10: 11 to 20' },
      { kind: 'p', text: 'Numbers from 11 to 20 often have unique names and patterns. They are:' },
      {
        kind: 'table',
        header: ['Number', 'Word', 'Pronunciation'],
        rows: [
          ['11', 'Eleven', '/ih-LEV-uhn/'],
          ['12', 'Twelve', '/twehl-VE/'],
          ['13', 'Thirteen', '/thr-TEEN/'],
          ['14', 'Fourteen', '/fohr-TEEN/'],
          ['15', 'Fifteen', '/fihf-TEEN/'],
          ['16', 'Sixteen', '/siks-TEEN/'],
          ['17', 'Seventeen', '/seh-vihn-TEEN/'],
          ['18', 'Eighteen', '/ayt-TEEN/'],
          ['19', 'Nineteen', '/nain-TEEN/'],
          ['20', 'Twenty', '/TWEN-tee/'],
        ],
      },
      {
        kind: 'note',
        text: 'The suffix “teen” comes from the word “ten” and means plus 10. So, “nineteen,” is nine plus 10, while “fifteen” is five plus 10.',
      },
      { kind: 'h3', text: 'Counting by tens: 20 to 100' },
      {
        kind: 'p',
        text: '20 to 100 are formed by combining tens with ones and are pronounced without pausing between the two words.',
      },
      {
        kind: 'table',
        header: ['Number', 'Word', 'Pronunciation'],
        rows: [
          ['21', 'Twenty-one', '/twehn-tee-WUHN/'],
          ['22', 'Twenty-two', '/twehn-tee-TOO/'],
          ['23', 'Twenty-three', '/twehn-tee-THREE/'],
          ['24', 'Twenty-four', '/twehn-tee-FOHR/'],
          ['25', 'Twenty-five', '/twehn-tee-FAIV/'],
          ['26', 'Twenty-six', '/twehn-tee-SIKS/'],
          ['27', 'Twenty-seven', '/twehn-tee-SEH-vihn/'],
          ['28', 'Twenty-eight', '/twehn-tee-AYT/'],
          ['29', 'Twenty-nine', '/twehn-tee-NAIN/'],
          ['30', 'Thirty', '/THR-tee/'],
          ['40', 'Forty', '/FOHR-tee/'],
          ['50', 'Fifty', '/FIF-tee/'],
          ['60', 'Sixty', '/SIKS-tee/'],
          ['70', 'Seventy', '/SEV-vihn-tee/'],
          ['80', 'Eighty', '/AY-tee/'],
          ['90', 'Ninety', '/NAIN-tee/'],
          ['100', 'One hundred or hundred', '/wuhn-HUN-druhd/'],
        ],
      },
      { kind: 'h3', text: 'Counting in hundreds and thousands' },
      { kind: 'p', text: 'Exploring large numbers in English' },
      {
        kind: 'p',
        text: 'After “thousand”, we also have “million”, “billion”, “trillion”, “quadrillion”, “quintillion”, etc. To read and pronounce large numbers, we break them into parts:',
      },
      {
        kind: 'p',
        text: '1,234,567,890 = one billion, two hundred thirty-four million, five hundred sixty-seven thousand, eight hundred ninety',
      },
    ],
  },
  {
    slug: 'ordinal-numbers-dates',
    title: 'Numbers: Ordinal Numbers & Dates',
    blocks: [
      {
        kind: 'p',
        text: 'Ordinal numbers in English signify order and rank. They are typically created by adding “th” at the end of a cardinal number. These numbers are used when you want to:',
      },
      { kind: 'p', text: 'a. Give a date in the calendar:' },
      { kind: 'examples', items: ['My birthday is on the **26th** of next month.'] },
      { kind: 'p', text: 'b. Put things in order:' },
      { kind: 'examples', items: ['He is the **fifth** on the list.'] },
      { kind: 'p', text: 'c. Refer to centuries:' },
      { kind: 'examples', items: ['The museum was established in the **18th** century.'] },
      { kind: 'p', text: 'd. Refer to a floor of a building:' },
      { kind: 'examples', items: ['My office is on the **10th** floor.'] },
      { kind: 'h3', text: 'Forming ordinal numbers from cardinal numbers' },
      {
        kind: 'p',
        text: 'As mentioned above, we form most ordinal numbers by adding the suffix “th” to cardinal numbers. The only exceptions are “first”, “second”, and “third”.',
      },
      {
        kind: 'table',
        header: ['Number', 'Ordinal number'],
        rows: [
          ['1st', 'First'],
          ['2nd', 'Second'],
          ['3rd', 'Third'],
          ['4th', 'Fourth'],
          ['5th', 'Fifth /fith/'],
          ['6th', 'Sixth'],
          ['7th', 'Seventh'],
          ['8th', 'Eighth'],
          ['9th', 'Ninth'],
          ['10th', 'Tenth'],
          ['11th', 'Eleventh'],
          ['12th', 'Twelfth /twelth/'],
          ['13th', 'Thirteenth'],
          ['14th', 'Fourteenth'],
          ['15th', 'Fifteenth'],
          ['16th', 'Sixteenth'],
          ['17th', 'Seventeenth'],
          ['18th', 'Eighteenth'],
          ['19th', 'Nineteenth'],
          ['20th', 'Twentieth'],
          ['21st', 'Twenty-first'],
          ['22nd', 'Twenty-second'],
          ['23rd', 'Twenty-third'],
          ['30th', 'Thirtieth'],
          ['40th', 'Fortieth'],
          ['50th', 'Fiftieth'],
          ['60th', 'Sixtieth'],
          ['70th', 'Seventieth'],
          ['80th', 'Eightieth'],
          ['90th', 'Ninetieth'],
          ['100th', 'Hundredth /hundreth/'],
        ],
      },
      {
        kind: 'note',
        text: 'When writing out large numbers in ordinals, we change the last numeral to an ordinal number. For example:',
      },
      {
        kind: 'list',
        items: ['104th – **One hundred fourth**', '287th – **Two hundred eighty-seventh**'],
      },
    ],
  },
  {
    slug: 'talking-about-money',
    title: 'Numbers: Talking About Money',
    blocks: [
      {
        kind: 'p',
        text: 'Talking about money and prices is an important part of everyday language. Here we can learn how to talk about money and prices. Money can come in different forms, such as paper, coins, and digital currencies.',
      },
      { kind: 'h3', text: '1. One, Two, Three Digit Prices' },
      {
        kind: 'list',
        items: [
          'a. We can just say the numbers: $2.50 → **two-fifty**',
          'b. We can mention the unit: $2.50 → **two dollars fifty**',
          'c. We can say both the units and the numbers: $2.50 → **two dollars and fifty cents**',
        ],
      },
      {
        kind: 'p',
        text: 'd. We should know that if the price is an exact number of pounds, euros or dollars, we should say it completely.',
      },
      {
        kind: 'examples',
        items: [
          '$600.00 → **six hundred dollars**',
          '£560.00 → **five hundred and sixty pounds**',
        ],
      },
      {
        kind: 'p',
        text: 'e. When the price has \'one\' at the beginning, we can use \'a\' instead of \'one\', for instance: £100 → a hundred pounds',
      },
      { kind: 'h3', text: '2. Four Digit Prices' },
      {
        kind: 'p',
        text: 'a. Typically, we read these prices in the same manner as described above.',
      },
      {
        kind: 'examples',
        items: [
          '$1458.20 → **a thousand four hundred fifty eight, twenty**',
          '$4250 → **four thousand two hundred fifty dollars**',
        ],
      },
      {
        kind: 'p',
        text: 'b. If the last two digits are zeros, we can say the price in two ways:',
      },
      {
        kind: 'examples',
        items: [
          '$1200 → \'**twelve hundred**\'',
          '$1200 → \'**a thousand two hundred**\'',
          '£4500 → \'**forty five hundred pounds**” or ‘**four thousand five hundred pounds**’',
          'R$ 8800 → ‘**eighty eight hundred reais**’ or ‘**eight thousand eight hundred reais**’',
        ],
      },
      { kind: 'h3', text: '3. Prices with More than Four Digits' },
      {
        kind: 'p',
        text: 'The general rule here is the same as what we have discussed so far:',
      },
      {
        kind: 'examples',
        items: [
          '$1,000,000 → **a million dollars**',
          '$2,450,300 → **two million and four hundred fifty thousand and three hundred**',
          '$1,000,000,000 → **a billion dollars**',
          '$1,340,652,500 → **one billion and three hundred forty million and six hundred fifty two thousand and five hundred**',
          '$1,000,000,000,000 → **a trillion dollars**',
        ],
      },
    ],
  },
  {
    slug: 'irregular-plural-nouns',
    title: 'Irregular Plural Nouns',
    blocks: [
      {
        kind: 'p',
        text: 'Although most nouns have plurals formed according to regular rules, some nouns have unusual, or irregular plurals. The following chart will introduce the most common ones so that you can learn them.',
      },
      {
        kind: 'table',
        header: ['Noun type', 'Forming the plural', 'Example'],
        rows: [
          [
            'Ends with -fe',
            'Change f to v, then add -s',
            'knife → **knives** /naives/; life → **lives** /laivs/; wife → **wives**',
          ],
          [
            'Ends with -f',
            'Change f to v, then add -es',
            'half /héf/ → **halves** /hévs/; wolf → **wolves**; loaf → **loaves**; leaf → **leaves**',
          ],
          [
            'Ends with -o',
            'Add -es',
            'potato → **potatoes**; tomato → **tomatoes**; volcano → **volcanoes**',
          ],
          [
            'Ends with -us',
            'Change -us to -i',
            'cactus → **cacti**; nucleus → **nuclei**; focus → **foci**; campus → **campi**',
          ],
          [
            'Ends with -is',
            'Change -is to -es',
            'analysis → **analyses**; crisis → **crises**; thesis → **theses** (same pronunciation)',
          ],
          [
            'Ends with -on',
            'Change -on to -a',
            'phenomenon → **phenomena**; criterion → **criteria**; datum → **data**',
          ],
          [
            'Other irregular nouns',
            'Change the vowel or Change the word or Add a different ending',
            'man → **men** (same pronunciation); woman /uo/ → **women** /ui/; foot → **feet**; child → **children**; person → **people**; tooth → **teeth**; mouse → **mice**',
          ],
        ],
      },
    ],
  },
  {
    slug: 'countable-uncountable-nouns',
    title: 'Countable and Uncountable Nouns',
    blocks: [
      { kind: 'h3', text: 'a. Countable nouns' },
      {
        kind: 'p',
        text: 'Are nouns that exist both in singular and plural forms. Yes, we can count them, but this way of learning them does not make sense when we are speakers of another language. Therefore, the best way to understand is to think if they exist in two forms: one singular and one plural.',
      },
      {
        kind: 'examples',
        items: [
          'She has **three dogs**.',
          'I own **a house**.',
          'I would like **two books** please.',
          '**How many friends** do you have? I have **a few friends**.',
        ],
      },
      { kind: 'h3', text: 'b. Uncountable nouns' },
      {
        kind: 'p',
        text: 'Are nouns that only exist in one form, either singular or plural. Usually, they refer to substances, feelings, abstract ideas or qualities or physical objects that are too small or too amorphous to be counted (liquids, powders, gasses, etc.). If introduced by a verb, the verb should be in the singular form.',
      },
      {
        kind: 'examples',
        items: [
          'This is great **news**!',
          'That woman carries **a lot of knowledge** with her.',
          'Can I give you **a piece of advice**? Yes, you can give me **some advice**.',
          '**How much sugar** would you like in your coffee? Just **a little sugar**.',
        ],
      },
      {
        kind: 'p',
        text: 'Be careful with these uncountable nouns that look like countable, but AREN’T:',
      },
      {
        kind: 'list',
        items: [
          'bread',
          'feedback',
          'coffee',
          'information',
          'money',
          'advice',
          'luggage',
          'furniture',
        ],
      },
    ],
  },
  {
    slug: 'quantifiers',
    title: 'Quantifiers: A, An, Any, Some & A Lot Of',
    blocks: [
      {
        kind: 'p',
        text: 'Quantifiers are particles that help us have an idea of how much or how many of something there is.',
      },
      { kind: 'h3', text: '1. A vs. AN' },
      {
        kind: 'p',
        text: 'A and AN are used with singular countable nouns. A is used before consonant sounds and AN is used before vowel sounds.',
      },
      {
        kind: 'examples',
        items: [
          'There\'s **a man** at the door.',
          'Is there **an email address** to write to?',
        ],
      },
      { kind: 'h3', text: '2. SOME vs. ANY' },
      {
        kind: 'p',
        text: 'We use SOME with countable plural nouns and uncountable nouns in positive sentences. We use ANY with negative sentences either with countable or uncountable nouns.',
      },
      {
        kind: 'examples',
        items: [
          'There are **some people** in the concert. → countable noun, affirmative sentence',
          'There\'s **some milk** in the fridge. → uncountable noun, affirmative sentence',
          'There aren\'t **any seats**. → countable noun, negative sentence',
          'There isn\'t **any coffee**. → uncountable noun, negative sentence',
        ],
      },
      {
        kind: 'p',
        text: '3. Some quantifiers can be used with both countable and uncountable nouns: all, some, more, a lot of, enough, no, any, most, lots of, less, plenty of, a load of, tons of.',
      },
      {
        kind: 'examples',
        items: [
          'We have **lots of time**. → uncountable',
          'Joe has **lots of friends**. → countable',
          'I can\'t go out. I\'ve got **no money**. → uncountable',
          'There was **a lot of food** but **no drinks**. → countable',
        ],
      },
      {
        kind: 'p',
        text: '4. Some quantifiers can be used with only countable nouns: many, each, either, (a) few, several, both, neither, fewer, a couple of, hundreds of.',
      },
      {
        kind: 'examples',
        items: [
          'I’ll be back in **a couple of minutes**.',
          'There were **many people** at the meeting.',
          'I have **a few good friends** who I know are real.',
        ],
      },
      {
        kind: 'p',
        text: '5. Some quantifiers can be used with only uncountable nouns: much, a bit of, (a) little, a great deal of, a good deal of.',
      },
      {
        kind: 'examples',
        items: [
          'Would you like **a little wine**?',
          'Could I have **a bit of butter**, please?',
          'I can’t go out today, I don’t have **much money** left for the month!',
        ],
      },
    ],
  },
  {
    slug: 'how-many-vs-how-much',
    title: 'How Many vs. How Much',
    blocks: [
      {
        kind: 'p',
        text: 'Both of these expressions are used to ask about the quantity of something.',
      },
      {
        kind: 'p',
        text: '1. We use “how much” with uncountable nouns. These nouns are always singular and they cannot be counted.',
      },
      {
        kind: 'examples',
        items: [
          '**How much** money is left?',
          '**How much** sugar do you need for your tea?',
          '**How much** (money) does this shirt cost?',
        ],
      },
      {
        kind: 'p',
        text: '2. We use “how many” when the noun we are talking about is countable and has a plural form.',
      },
      {
        kind: 'examples',
        items: [
          '**How many** books have you read this year?',
          '**How many** plates do we need for tonight?',
          '**How many** cups of sugar do you need to make a cake?',
        ],
      },
      {
        kind: 'note',
        text: 'When asking about the price of something we always use “how much” because it implies that we are talking about “money”.',
      },
    ],
  },
];
